import React, { useState, useEffect } from 'react';
import { Settings, Volume2, VolumeX, Bell, BellOff, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

interface ChatSettings {
  soundEnabled: boolean;
  notificationsEnabled: boolean;
  darkMode: boolean;
  autoOpen: boolean;
  showTypingIndicator: boolean;
}

interface ChatSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  settings: ChatSettings;
  onSettingsChange: (settings: ChatSettings) => void;
}

export const ChatSettings: React.FC<ChatSettingsProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
}) => {
  const [localSettings, setLocalSettings] = useState<ChatSettings>(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSettingChange = (key: keyof ChatSettings, value: boolean) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const handleSave = () => {
    onSettingsChange(localSettings);
    onClose();
  };

  const handleReset = () => {
    const defaultSettings: ChatSettings = {
      soundEnabled: true,
      notificationsEnabled: true,
      darkMode: false,
      autoOpen: false,
      showTypingIndicator: true,
    };
    setLocalSettings(defaultSettings);
    onSettingsChange(defaultSettings);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configurações do Chat" size="md">
      <div className="space-y-6">
        {/* Som */}
        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
          <div className="flex items-center space-x-3">
            {localSettings.soundEnabled ? (
              <Volume2 size={20} className="text-green-600" />
            ) : (
              <VolumeX size={20} className="text-gray-400" />
            )}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-slate-100">Som de Notificação</h4>
              <p className="text-sm text-gray-500 dark:text-slate-400">Tocar som quando receber mensagens</p>
            </div>
          </div>
          <Button
            variant={localSettings.soundEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => handleSettingChange('soundEnabled', !localSettings.soundEnabled)}
          >
            {localSettings.soundEnabled ? 'Ativado' : 'Desativado'}
          </Button>
        </div>

        {/* Notificações */}
        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
          <div className="flex items-center space-x-3">
            {localSettings.notificationsEnabled ? (
              <Bell size={20} className="text-green-600" />
            ) : (
              <BellOff size={20} className="text-gray-400" />
            )}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-slate-100">Notificações Push</h4>
              <p className="text-sm text-gray-500 dark:text-slate-400">Mostrar notificações do navegador</p>
            </div>
          </div>
          <Button
            variant={localSettings.notificationsEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => handleSettingChange('notificationsEnabled', !localSettings.notificationsEnabled)}
          >
            {localSettings.notificationsEnabled ? 'Ativado' : 'Desativado'}
          </Button>
        </div>

        {/* Modo Escuro */}
        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
          <div className="flex items-center space-x-3">
            {localSettings.darkMode ? (
              <Moon size={20} className="text-blue-600" />
            ) : (
              <Sun size={20} className="text-yellow-600" />
            )}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-slate-100">Modo Escuro</h4>
              <p className="text-sm text-gray-500 dark:text-slate-400">Interface escura para o chat</p>
            </div>
          </div>
          <Button
            variant={localSettings.darkMode ? "default" : "outline"}
            size="sm"
            onClick={() => handleSettingChange('darkMode', !localSettings.darkMode)}
          >
            {localSettings.darkMode ? 'Ativado' : 'Desativado'}
          </Button>
        </div>

        {/* Abrir Automaticamente */}
        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
          <div className="flex items-center space-x-3">
            <Settings size={20} className="text-blue-600" />
            <div>
              <h4 className="font-medium text-gray-900 dark:text-slate-100">Abrir Automaticamente</h4>
              <p className="text-sm text-gray-500 dark:text-slate-400">Abrir chat quando receber mensagens</p>
            </div>
          </div>
          <Button
            variant={localSettings.autoOpen ? "default" : "outline"}
            size="sm"
            onClick={() => handleSettingChange('autoOpen', !localSettings.autoOpen)}
          >
            {localSettings.autoOpen ? 'Ativado' : 'Desativado'}
          </Button>
        </div>

        {/* Indicador de Digitação */}
        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-slate-100">Indicador de Digitação</h4>
              <p className="text-sm text-gray-500 dark:text-slate-400">Mostrar quando outros estão digitando</p>
            </div>
          </div>
          <Button
            variant={localSettings.showTypingIndicator ? "default" : "outline"}
            size="sm"
            onClick={() => handleSettingChange('showTypingIndicator', !localSettings.showTypingIndicator)}
          >
            {localSettings.showTypingIndicator ? 'Ativado' : 'Desativado'}
          </Button>
        </div>

        {/* Botões de Ação */}
        <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
          <Button variant="outline" onClick={handleReset}>
            Restaurar Padrões
          </Button>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
